import { useId } from 'react'
import styles from './DonutGauge.module.css'

/**
 * DonutGauge — a single-value ring gauge (0..max), drawn as two stacked SVG
 * circles (a full track + a partial progress arc via stroke-dasharray),
 * with an optional centered value/label.
 *
 * The progress arc is painted with a diagonal gradient sweep — `color`
 * itself at the bright end, a dimmed mix of it toward the app's dark
 * surface at the other — rather than a flat stroke. This is purely a
 * rendering choice inside the arc; every caller still just passes a single
 * `color`, so existing hero/gauge JSON payloads (see the `stat` block's
 * `gauge.color` field) need no changes to pick up the new look.
 *
 * Used wherever the design shows a circular "score" ring: the Home banner's
 * composite Execution Efficiency Gain ring, and the executive-priority /
 * path-effectiveness ring icons on the insight cards.
 *
 * Props:
 *   value       {number} — current value
 *   max         {number} — value at 100% fill. Defaults to 100.
 *   color       {string} — progress arc color (CSS color value) — also the
 *                          bright end of the gradient sweep
 *   trackColor  {string} — background ring color. Defaults to the app's
 *                          standard dark track color.
 *   size        {number} — outer diameter in px. Defaults to 96.
 *   strokeWidth {number} — ring thickness in px. Defaults to size/9.6.
 *   centerValue {string|number} — optional big text in the ring's center
 *   centerLabel {string} — optional small muted text under centerValue
 *   centerValueSize {string} — CSS font-size override for centerValue
 *                     (defaults to the module's fixed 1.15rem). Use this
 *                     when a longer centerLabel needs to fit a small ring
 *                     without overflowing/clipping.
 *   centerLabelSize {string} — CSS font-size override for centerLabel
 *                     (defaults to the module's fixed 0.62rem).
 */
export default function DonutGauge({
  value, max = 100, color = 'var(--color-semantic-blue)', trackColor = 'var(--color-blue-600)',
  size = 96, strokeWidth, centerValue, centerLabel, centerValueSize, centerLabelSize,
}) {
  const gradientId = useId()
  const sw = strokeWidth ?? Math.max(4, size / 9.6)
  const r = (size - sw) / 2
  const c = size / 2
  const circumference = 2 * Math.PI * r
  const pct = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0
  const dash = circumference * pct
  const dimColor = `color-mix(in srgb, ${color} 45%, var(--color-blue-900))`

  return (
    <div className={styles.wrap} style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={dimColor} />
            <stop offset="100%" stopColor={color} />
          </linearGradient>
        </defs>
        <circle cx={c} cy={c} r={r} fill="none" stroke={trackColor} strokeWidth={sw} />
        <circle
          cx={c} cy={c} r={r} fill="none" stroke={`url(#${gradientId})`} strokeWidth={sw}
          strokeDasharray={`${dash} ${circumference - dash}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${c} ${c})`}
        />
      </svg>
      {(centerValue != null || centerLabel) && (
        <div className={styles.center}>
          {centerValue != null && (
            <span className={styles.centerValue} style={{ color, ...(centerValueSize ? { fontSize: centerValueSize } : null) }}>
              {centerValue}
            </span>
          )}
          {centerLabel && (
            <span className={styles.centerLabel} style={centerLabelSize ? { fontSize: centerLabelSize } : undefined}>
              {centerLabel}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
